import { ReferencedDocument, VirusScanStatus } from '@qtek/shared/models';

/** SD-3016: where an attached file is, from being picked to Allo being able to read it. */
export type ChatFileStep =
  | 'uploading'
  | 'scanning'
  | 'reading'
  | 'ready'
  | 'failed';

export interface ChatFileVM {
  /** Local key, never the file name: two files can share a name. */
  key: string;
  name: string;
  /** MIME type. */
  type: string;
  step: ChatFileStep;
  /** Upload progress, 0-100. Only meaningful while uploading. */
  progress: number;
  /** Why it failed: the upload broke (can be tried again) or the virus scan blocked it (can't). */
  failure?: 'upload' | 'blocked';
  /** ChatDocumentReference._id, once uploaded. */
  referenceId?: string;
  /** Document._id, once uploaded. What the message sends. */
  documentId?: string;
}

const SCANNER = 'ClamAvScanner';
const TEXT_READER = 'DocumentTextExtractor';

type ProcessingFields = Pick<
  ReferencedDocument,
  'virusScanStatus' | 'executorPlan' | 'executorDone' | 'executorFailed'
>;

/**
 * The step an uploaded file is on, by the rules the backend uses to decide whether Allo can read
 * it yet (`ChatAttachmentContentService.processed` and `waitingLabel`). Only the scan and the text
 * reading hold up the answer; the later steps (embeddings, contract readers) don't, so they read
 * as ready. A failed text read is ready too: the backend then reads the file as it is.
 */
export function processingStep(doc: ProcessingFields): ChatFileStep {
  const scan = doc.virusScanStatus;
  if (
    scan === VirusScanStatus.CONTAMINATED ||
    scan === VirusScanStatus.FAILED
  ) {
    return 'failed';
  }
  const plan = doc.executorPlan;
  if (!plan) {
    // No plan: processing hasn't started, unless the file is already clean.
    return scan == null || scan === VirusScanStatus.OK ? 'ready' : 'scanning';
  }
  const settled = (step: string) =>
    !plan.includes(step) ||
    (doc.executorDone ?? []).includes(step) ||
    (doc.executorFailed ?? []).includes(step);
  if (!settled(SCANNER)) return 'scanning';
  if (!settled(TEXT_READER)) return 'reading';
  return 'ready';
}

/** What the chip says about the file's step. Empty when there's nothing to say. */
export function chatFileStatusLabel(file: ChatFileVM): string {
  switch (file.step) {
    case 'uploading':
      return `Uploading… ${file.progress}%`;
    case 'scanning':
      return 'Scanning for viruses…';
    case 'reading':
      return 'Reading…';
    case 'failed':
      return file.failure === 'blocked'
        ? 'Blocked by the virus scan'
        : `${file.name} couldn't be uploaded`;
    default:
      return '';
  }
}

const TYPE_LABELS: Record<string, string> = {
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'DOCX',
  'application/vnd.ms-excel': 'XLS',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.ms-powerpoint': 'PPT',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'PPTX',
  'text/plain': 'TXT',
  'text/markdown': 'MD',
};

/** Short type tag for a MIME type ("PDF", "DOCX"). */
export function fileTypeLabel(type: string): string {
  if (!type) return '';
  return TYPE_LABELS[type] ?? (type.split('/').pop() ?? type).toUpperCase();
}
